import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api } from "../lib/api";

/**
 * Themed-effect picker grid — Sky Drop, Helicopter Reveal, Cozy
 * Evening, Halloween, etc. Each card applies a curated bundle of
 * `template_id + mood_ids + intensity + image_transform + transition_style`
 * via the `onPick` callback.
 *
 * Pure UX layer — zero new infra under the hood.
 */
export default function EffectLibrary({
  selectedId,
  onPick,
  className = "",
}) {
  const [data, setData] = useState({ effects: [], categories: [] });
  const [activeCat, setActiveCat] = useState(null);

  useEffect(() => {
    api.get("/effect-library")
      .then(({ data }) => {
        setData(data || { effects: [], categories: [] });
        if (data?.categories?.length) {
          setActiveCat(data.categories[0].id);
        }
      })
      .catch(() => {});
  }, []);

  const filtered = (data.effects || []).filter(
    (e) => !activeCat || e.category === activeCat
  );

  const iconFor = (name) => {
    const I = Icons[name];
    return I || Icons.Sparkles;
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Category chips */}
      <div className="flex flex-wrap gap-1.5">
        {(data.categories || []).map((c) => (
          <button
            key={c.id}
            onClick={() => setActiveCat(c.id)}
            className={`px-2.5 py-1 rounded-full text-[10px] border transition-all ${
              activeCat === c.id
                ? "bg-amber-500/15 border-amber-500/50 text-amber-200"
                : "bg-black border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"
            }`}
            data-testid={`effect-cat-${c.id}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Effect cards grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {filtered.map((e) => {
          const I = iconFor(e.icon);
          const isSel = e.id === selectedId;
          return (
            <button
              key={e.id}
              onClick={() => onPick(e)}
              className={`text-left rounded-lg border p-3 transition-all flex flex-col gap-1.5 ${
                isSel
                  ? "bg-amber-500/10 border-amber-500/50"
                  : "bg-black/40 border-white/5 hover:border-white/20 hover:bg-black/60"
              }`}
              data-testid={`effect-card-${e.id}`}
            >
              <div className="flex items-center justify-between">
                <div
                  className={`w-7 h-7 rounded-md flex items-center justify-center ${
                    isSel ? "bg-amber-500/20 text-amber-300" : "bg-white/5 text-zinc-400"
                  }`}
                >
                  <I size={13} />
                </div>
                {isSel && (
                  <Icons.Check size={13} className="text-amber-300" />
                )}
              </div>
              <div className="text-[12px] text-white font-medium leading-tight">
                {e.name}
              </div>
              <div className="text-[10px] text-zinc-500 leading-snug line-clamp-2">
                {e.blurb}
              </div>
              {e.needs_aerial_source && (
                <div className="text-[9px] text-amber-400/80 flex items-center gap-1 mt-0.5">
                  <Icons.Plane size={9} />
                  needs aerial-perspective source
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
